import { type LessonItem, classesNow, shortDate, timeOf } from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { useTranslation } from 'react-i18next';
import { DEEP, GRAD, type Grad, INK, INK_FAINT, INK_SOFT, SubjectArt, grad } from '../design/forms';
import { useNow } from './AlarmBanner';
import { Panel } from './ui';

/*
 * «Класи зараз» in «Форми 2» (28.10): the director's one look at what every class is doing. What matters
 * is where they are, so the colour is the format — in the class a calm stone, online lavender, in the
 * shelter butter — the class big and light, the subject with its picture big and quiet behind, now or
 * when next.
 */

const FORMAT_GRAD: Record<LessonItem['format'], Grad> = {
  in_person: 'stone',
  online: 'lavender',
  shelter: 'butter',
};

/** The director's one screen: what every class is doing now — in person, online, in the shelter. */
export function ClassesNow({
  lessons,
  date,
  isToday,
}: {
  lessons: ReadonlyArray<LessonItem>;
  date: string;
  isToday: boolean;
}) {
  const { t } = useTranslation();
  const now = useNow();
  // on another day: the classes as planned (their first lesson)
  const classes = classesNow(lessons, isToday ? now : 0);
  if (classes.length === 0) return null;
  return (
    <Panel
      title={isToday ? t('classesNow.title') : t('classesNow.planned', { day: shortDate(date) })}
    >
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {classes.map((c) => {
          const tone: Grad = c.lesson ? FORMAT_GRAD[c.lesson.format] : 'stone';
          const look = c.lesson ? subjectLook(c.lesson.subject) : undefined;
          return (
            <div
              key={c.classId}
              className={`relative isolate min-h-[96px] overflow-hidden rounded-[18px] p-3 ${c.lesson ? '' : 'opacity-60'}`}
              style={{ backgroundImage: grad(tone) }}
            >
              {look && (
                <SubjectArt
                  icon={look.icon}
                  tone={tone}
                  size={64}
                  ghost
                  className="pointer-events-none absolute -right-2 -bottom-3 -z-10"
                />
              )}
              <p
                className="font-display text-[24px] leading-7 font-light tracking-[-0.03em]"
                style={{ color: INK }}
              >
                {c.classLabel}
              </p>
              {c.lesson ? (
                <>
                  <p
                    className="mt-0.5 truncate text-[13.5px] font-medium"
                    style={{ color: tone === 'stone' ? INK : GRAD[tone].ink }}
                  >
                    {c.lesson.subject}
                  </p>
                  <p
                    className="mt-0.5 flex items-center gap-1.5 text-[12px]"
                    style={{ color: INK_SOFT }}
                  >
                    {c.live && (
                      <span
                        aria-hidden
                        className="size-1.5 shrink-0 rounded-full"
                        style={{ backgroundColor: DEEP.mint[1] }}
                      />
                    )}
                    <span className="truncate">
                      {t(`classesNow.formats.${c.lesson.format}`)} ·{' '}
                      {c.live
                        ? t('classesNow.live')
                        : t('classesNow.next', { time: timeOf(c.lesson.startsAt) })}
                    </span>
                  </p>
                </>
              ) : (
                <p className="mt-0.5 text-[12px]" style={{ color: INK_FAINT }}>
                  {t('classesNow.done')}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
